import {
  CloudIcon,
  InformationCircleIcon,
  XMarkIcon,
} from '@heroicons/react/24/outline';
import { Button } from '@/components/chariot-ui';
import React, { useEffect, useRef, useState } from 'react';

import { Tooltip } from '@/components/Tooltip';
import { Container } from '@/components/ui';
import { NoData } from '@/components/ui/NoData';
import { Agent } from '@/hooks/useAgents';
import { EmptyStateContainer } from '@/sections/agents/components/aegis-management/shared/EmptyStateContainer';
import { TIMEOUTS } from '@/sections/agents/constants';
import { formatDate } from '@/utils/date.util';

import { ProvisionTunnelModal } from './ProvisionTunnelModal';
import { RemoveTunnelModal } from './RemoveTunnelModal';
import { useTunnelManagement } from './useTunnelManagement';

interface TunnelManagementTabProps {
  agent: Agent;
  taskMonitoring: ReturnType<
    typeof import('@/sections/agents/hooks').useTaskMonitoring
  >;
  setSelectedTabValue: (value: string) => void;
  isModalOpen: boolean;
}

/**
 * Component for managing Cloudflare tunnel operations
 *
 * This component combines the original TunnelManagementTab and its container,
 * managing tunnel state and operations in a single component.
 *
 * Provides interface for:
 * - Viewing tunnel status and configuration
 * - Provisioning new tunnels
 * - Removing existing tunnels
 * - Displaying tunnel details (hostname, tunnel name, health check)
 */
export const TunnelManagementTab: React.FC<TunnelManagementTabProps> = ({
  agent,
  taskMonitoring: _taskMonitoring,
  setSelectedTabValue,
  isModalOpen,
}) => {
  // Local tunnel management state
  const tunnelManagement = useTunnelManagement(isModalOpen);

  // Track if we're waiting for tunnel configuration to complete
  const [awaitingConfiguration, setAwaitingConfiguration] = useState(false);

  // Timer ref for setTimeout cleanup
  const timerRef = useRef<ReturnType<typeof setTimeout> | null>(null);

  // Cleanup timer on unmount
  useEffect(() => {
    return () => {
      if (timerRef.current) clearTimeout(timerRef.current);
    };
  }, []);

  // Modal visibility state
  const [showProvisionModal, setShowProvisionModal] = useState(false);
  const [showRemoveModal, setShowRemoveModal] = useState(false);

  // Get tunnel info from the tunnel management hook
  const tunnelInfo = tunnelManagement.getTunnelInfo(agent);

  // Monitor when tunnel becomes configured
  React.useEffect(() => {
    if (tunnelInfo.status === 'configured' && awaitingConfiguration) {
      // Tunnel is now configured, clear the awaiting flag
      setAwaitingConfiguration(false);
    }
  }, [tunnelInfo.status, awaitingConfiguration]);

  if (!agent) return null;

  // Handler for successful provision
  const handleProvisionSuccess = (taskId?: string) => {
    // Set awaiting configuration flag for better UX
    setAwaitingConfiguration(true);

    // Cancel any existing timer (handles repeated calls)
    if (timerRef.current) clearTimeout(timerRef.current);

    // Clear the flag after configured timeout
    timerRef.current = setTimeout(() => {
      setAwaitingConfiguration(false);
      timerRef.current = null;
    }, TIMEOUTS.TUNNEL_CONFIG);

    // Switch to tasks tab if installation task was queued
    if (taskId) {
      setSelectedTabValue('tasks');
    }
  };

  // Handler for successful removal
  const handleRemoveSuccess = (taskId?: string) => {
    // Switch to tasks tab if removal task was created
    if (taskId) {
      setSelectedTabValue('tasks');
    }
  };

  return (
    <div
      className="flex h-full flex-col gap-6"
      data-testid="tunnel-tab-content"
    >
      {/* Status Section */}
      <Container
        className="border-chariot-stroke bg-chariot-background-primary flex h-full flex-1 flex-col rounded-md border p-6"
        data-testid="tunnel-status-container"
      >
        {/* Header - ALWAYS visible with action button when tunnel configured */}
        <div className="mb-4 flex items-center justify-between">
          <div className="flex items-center gap-2">
            <h3 className="text-chariot-text-primary text-lg font-semibold">
              Cloudflare Tunnel
            </h3>
            <Tooltip title="Secure SSH access via Cloudflare tunnel. Requires Cloudflare Access authentication. Create users in Users tab.">
              <div className="text-chariot-text-secondary cursor-help">
                <InformationCircleIcon className="size-4" />
              </div>
            </Tooltip>
          </div>
          {/* Remove button shown only when tunnel is configured */}
          {tunnelInfo.status !== 'not_configured' && (
            <Button
              styleType="primary"
              size="sm"
              label="Remove Tunnel"
              onClick={() => setShowRemoveModal(true)}
              disabled={tunnelManagement.isLoading}
              startIcon={<XMarkIcon className="size-4" />}
              data-testid="remove-tunnel-button"
              aria-label="Remove Cloudflare tunnel and disable SSH access"
            />
          )}
        </div>

        {tunnelInfo.status === 'not_configured' ? (
          /* Empty State - No Tunnel Configured */
          <EmptyStateContainer>
            <NoData
              icon={<CloudIcon />}
              title="No Tunnel Configured"
              description="Provision a Cloudflare tunnel to enable secure SSH access without exposing ports."
              action={
                <Button
                  styleType="primary"
                  size="sm"
                  label="Provision Tunnel"
                  onClick={() => setShowProvisionModal(true)}
                  disabled={tunnelManagement.isLoading}
                  startIcon={<CloudIcon className="size-4" />}
                  data-testid="provision-tunnel-button"
                  aria-label="Provision Cloudflare tunnel for secure SSH access"
                />
              }
            />
          </EmptyStateContainer>
        ) : (
          /* Configured State - Show Tunnel Details */
          <div className="flex flex-col gap-4">
            {tunnelInfo.hostname && (
              <div
                className="bg-chariot-background-secondary mt-4 flex flex-col gap-3 rounded-md p-4"
                data-testid="tunnel-details"
              >
                <div className="flex justify-between">
                  <span className="text-chariot-text-secondary text-sm">
                    Hostname:
                  </span>
                  <span
                    className="text-chariot-text-primary text-sm font-medium"
                    data-testid="tunnel-hostname"
                  >
                    {tunnelInfo.hostname}
                  </span>
                </div>
                {tunnelInfo.tunnelName && (
                  <div className="flex justify-between">
                    <span className="text-chariot-text-secondary text-sm">
                      Tunnel Name:
                    </span>
                    <span
                      className="text-chariot-text-primary text-sm font-medium"
                      data-testid="tunnel-name"
                    >
                      {tunnelInfo.tunnelName}
                    </span>
                  </div>
                )}
                {tunnelInfo.lastHealthCheck && (
                  <div className="flex justify-between">
                    <span className="text-chariot-text-secondary text-sm">
                      Last Health Check:
                    </span>
                    <span
                      className="text-chariot-text-primary text-sm font-medium"
                      data-testid="tunnel-last-health-check"
                    >
                      {formatDate(tunnelInfo.lastHealthCheck)}
                    </span>
                  </div>
                )}
              </div>
            )}
          </div>
        )}
      </Container>

      {/* Provision Tunnel Modal */}
      <ProvisionTunnelModal
        isOpen={showProvisionModal}
        onClose={() => setShowProvisionModal(false)}
        agent={agent}
        onSuccess={handleProvisionSuccess}
      />

      {/* Remove Tunnel Modal */}
      <RemoveTunnelModal
        isOpen={showRemoveModal}
        onClose={() => setShowRemoveModal(false)}
        agent={agent}
        tunnelHostname={tunnelInfo.hostname || ''}
        tunnelName={tunnelInfo.tunnelName}
        onSuccess={handleRemoveSuccess}
      />
    </div>
  );
};
